import { apiEmploymentTypes } from "@/api/employment-types.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException } from "@/types/api.types.ts";
import { ApiEmploymentTypeSavePayload } from "@/types/employment-types.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";
import { useMemo, useState } from "react";
import { SelectItem } from "@skailer-com/uikit";
import { escapeRegExp } from "lodash";

const employmentTypesKey = createQueryKeys("employmentTypes", {
  all: null,
});

export const useEmploymentTypes = (enabled: boolean = true) => {
  return useQuery({
    ...employmentTypesKey.all,
    queryFn: async () => {
      const result = await apiEmploymentTypes.getAll();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    staleTime: 60 * 1000,
    enabled,
  });
};

export const useEmploymentTypesSearchInline = (enabled: boolean = true) => {
  const [searchKey, setSearchKey] = useState<string | undefined>(undefined);

  const { data, isLoading } = useEmploymentTypes(enabled);

  const options = useMemo(() => {
    const allData = (data || []) as SelectItem[];
    const regex = new RegExp(escapeRegExp(searchKey), "i");

    return allData.filter((option) => regex.test(option.title));
  }, [data, searchKey]);

  function search(text: string | undefined) {
    setSearchKey(text);
  }

  return {
    data,
    isLoading,
    options,
    search,
  };
};

export interface SaveParams {
  id: number | undefined;
  payload: ApiEmploymentTypeSavePayload;
}

export const useEmploymentTypesSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ id, payload }: SaveParams) => {
      const result = id
        ? await apiEmploymentTypes.update(id, payload)
        : await apiEmploymentTypes.create(payload);

      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employmentTypesKey.all.queryKey });
    },

    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useEmploymentTypesDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiEmploymentTypes.delete(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employmentTypesKey.all.queryKey });
    },

    onError: (error: Error) => {
      onError(error);
    },
  });
};
